import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import { suggestLabelValueMapping } from "./PaymentPlatformLabelDialog";

const refKey = (reference) => reference
  ? `${reference.fileName}\u0000${reference.sheetName}\u0000${reference.column}`
  : "";
const sourceKey = (reference) => reference
  ? `${reference.fileName}\u0000${reference.sheetName}`
  : "";
const normalizeColumnName = (value) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
const MERCHANT_KEY_ALIASES = [
  "msid", "merchantid", "externalid", "wpaccountid", "inputdefaultcompanyid",
  "accountid", "applicationid", "opportunityid", "registrationnumber", "ein", "taxid",
];
const OUTPUT_TEMPLATES = [
  { id: "repayment_outcome", name: "Positive / negative outcome", type: "boolean" },
  { id: "recommended_advance", name: "Recommended advance amount", type: "number" },
  { id: "pd_score", name: "PD score", type: "ordinal", min: 1, max: 5 },
];

export const initialMappedFields = (mapping, preview) => {
  const savedFields = mapping?.fields || {};
  return Object.values(savedFields).some(Boolean)
    ? savedFields
    : (preview?.suggestedMapping?.fields || {});
};

export const safeInitialJoinKeys = (mapping, preview) => {
  const profiles = preview?.profiles || [];
  const configured = mapping?.merge?.joinKeys?.length
    ? mapping.merge.joinKeys
    : (preview?.suggestedMapping?.merge?.joinKeys || []);
  if (!profiles.length) return configured;

  return profiles.map((profile) => {
    const columns = profile.columns || [];
    const selected = configured.find((reference) => sourceKey(reference) === sourceKey(profile));
    const selectedColumn = columns.find((column) => normalizeColumnName(column.name) === normalizeColumnName(selected?.column));
    const canonical = MERCHANT_KEY_ALIASES
      .map((alias) => columns.find((column) => normalizeColumnName(column.name) === alias))
      .find(Boolean);
    const selectedCardinality = Number(selectedColumn?.distinctCount || 0) / Math.max(1, Number(profile.rowCount || 0));

    if (canonical && (!selectedColumn || (normalizeColumnName(canonical.name) !== normalizeColumnName(selectedColumn.name)
      && selectedCardinality < 0.5
      && Number(canonical.distinctCount || 0) > Number(selectedColumn.distinctCount || 0)))) {
      return { fileName: profile.fileName, sheetName: profile.sheetName, column: canonical.name };
    }
    return selected || null;
  }).filter(Boolean);
};

export const initialMergeConfig = (mapping, preview) => {
  const suggested = preview?.suggestedMapping?.merge || {};
  return {
    joinKeys: safeInitialJoinKeys(mapping, preview),
    retainedColumns: mapping?.merge?.retainedColumns?.length
      ? mapping.merge.retainedColumns
      : (preview?.combinedColumns || suggested.retainedColumns || []),
  };
};

function FieldSelect({ field, value, options, onChange }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "250px 1fr" }, gap: 1.5, alignItems: "center", py: 0.75 }}>
      <Box>
        <Typography sx={{ fontWeight: 800, color: "#17365f", fontSize: 13.5 }}>{field.label}</Typography>
        <Chip
          size="small"
          label={field.importance}
          color={field.importance === "required" ? "primary" : "default"}
          sx={{ height: 20, fontSize: 10.5, textTransform: "capitalize" }}
        />
      </Box>
      <FormControl fullWidth size="small">
        <InputLabel>{field.label} column</InputLabel>
        <Select
          label={`${field.label} column`}
          value={refKey(value)}
          onChange={(event) => onChange(options.find((option) => refKey(option) === event.target.value) || null)}
        >
          <MenuItem value=""><em>Not mapped</em></MenuItem>
          {options.map((option) => (
            <MenuItem key={refKey(option)} value={refKey(option)}>
              {option.fileName} · {option.sheetName} · {option.column}
              <span style={{ marginLeft: "auto", color: "#64748b", paddingLeft: 12 }}>{option.coverage}%</span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default function PaymentPlatformColumnMappingDialog({
  open,
  preview,
  currentMapping,
  busy = false,
  onClose,
  onApply,
  onSaveDraft,
}) {
  const profiles = useMemo(() => preview?.profiles || [], [preview]);
  const mappingFields = useMemo(() => preview?.mappingFields || [], [preview]);
  const options = useMemo(
    () => profiles.flatMap((profile) => (profile.columns || []).map((column) => ({
      fileName: profile.fileName,
      sheetName: profile.sheetName,
      column: column.name,
      coverage: column.coverage,
      distinctCount: column.distinctCount,
      values: column.values || [],
      valuesTruncated: column.valuesTruncated,
    }))),
    [profiles],
  );
  const [fields, setFields] = useState({});
  const [joinKeys, setJoinKeys] = useState([]);
  const [retainedColumns, setRetainedColumns] = useState([]);
  const [outputs, setOutputs] = useState(OUTPUT_TEMPLATES.map((output) => ({ ...output, enabled: false })));
  const [runAll, setRunAll] = useState(true);
  const [step, setStep] = useState(0);

  const initialize = (mapping) => {
    const nextFields = initialMappedFields(mapping, preview);
    setFields(nextFields);
    const nextMerge = initialMergeConfig(mapping, preview);
    setJoinKeys(nextMerge.joinKeys);
    setRetainedColumns(nextMerge.retainedColumns);
    const storedOutputs = mapping?.outputs?.length
      ? mapping.outputs
      : (mapping?.label ? [{ ...OUTPUT_TEMPLATES[0], ...mapping.label }] : []);
    setOutputs(OUTPUT_TEMPLATES.map((template) => {
      const stored = storedOutputs.find((output) => output.id === template.id || output.type === template.type);
      if (!stored) return { ...template, enabled: false };
      const referenceOption = options.find((option) => refKey(option) === refKey(stored));
      const displayValue = (storedValue) =>
        referenceOption?.values?.find(
          (item) => String(item.value).trim().toLowerCase() === String(storedValue).trim().toLowerCase(),
        )?.value || storedValue;
      return {
        ...template,
        ...stored,
        enabled: true,
        positiveValues: (stored.positiveValues || []).map(displayValue),
        negativeValues: (stored.negativeValues || []).map(displayValue),
      };
    }));
    setRunAll(mapping?.runAll !== false);
    setStep(0);
  };

  useEffect(() => {
    if (open && preview) initialize(currentMapping);
  }, [open, preview, currentMapping]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateOutput = (id, changes) => {
    setOutputs((current) => current.map((output) => output.id === id ? { ...output, ...changes } : output));
  };
  const chooseOutputColumn = (output, reference) => {
    const suggestion = output.type === "boolean" ? suggestLabelValueMapping(reference?.values || []) : {};
    updateOutput(output.id, {
      ...(reference || { fileName: "", sheetName: "", column: "" }),
      ...(output.type === "boolean" ? suggestion : {}),
    });
  };

  const enabledOutputs = outputs.filter((output) => output.enabled);
  const outputReady = (output) => Boolean(
    output.column
    && (output.type !== "boolean" || (output.positiveValues?.length && output.negativeValues?.length)),
  );
  const readyOutputs = enabledOutputs.filter(outputReady);
  const joinReady = profiles.length > 0 && profiles.every((profile) => joinKeys.some(
    (reference) => sourceKey(reference) === sourceKey(profile),
  ));
  const columnsReady = retainedColumns.length > 0;
  const outputsReady = enabledOutputs.every(outputReady);
  const ready = joinReady && columnsReady && outputsReady;
  const mappedCount = Object.keys(fields).filter((key) => fields[key]).length;

  const mappingPayload = () => ({
    fields,
    outputs: enabledOutputs.filter((output) => output.column).map(({ enabled, ...output }) => output),
    runAll,
    merge: { joinKeys, retainedColumns },
  });
  const apply = () => onApply(mappingPayload());
  const continueToNextStep = () => {
    if (step === 1) {
      const kept = new Set(retainedColumns.map(refKey));
      setFields((current) => Object.fromEntries(
        Object.entries(current).filter(([, reference]) => kept.has(refKey(reference))),
      ));
      setOutputs((current) => current.map((output) => (
        output.column && !kept.has(refKey(output))
          ? { ...output, enabled: false, fileName: "", sheetName: "", column: "" }
          : output
      )));
    }
    setStep((value) => value + 1);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        MCA data column mapping
        <Button
          startIcon={<AutoFixHighOutlinedIcon />}
          onClick={() => initialize({ fields: preview?.suggestedMapping?.fields || {}, outputs: [] })}
          disabled={busy}
        >
          Auto-map columns
        </Button>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f7f9fc" }}>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {["Match merchants", "Choose columns", "Map model fields"].map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {step === 0 && <>
          <Alert severity={joinReady ? "success" : "info"} sx={{ mb: 2 }}>
            Select the column in each file that identifies the same merchant. The column names may be different, but their values must match.
          </Alert>
          <Stack spacing={1.5}>
            {profiles.map((profile) => {
              const selected = joinKeys.find((reference) => sourceKey(reference) === sourceKey(profile));
              const profileOptions = options.filter((option) => sourceKey(option) === sourceKey(profile));
              return (
                <Paper key={sourceKey(profile)} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography sx={{ fontWeight: 900, color: "#0b2a4a", mb: 0.5 }}>
                    {profile.fileName} · {profile.sheetName}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 12.5, mb: 1.5 }}>
                    {profile.rowCount.toLocaleString()} rows
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel>Merchant matching column</InputLabel>
                    <Select
                      label="Merchant matching column"
                      value={refKey(selected)}
                      onChange={(event) => {
                        const reference = profileOptions.find((option) => refKey(option) === event.target.value);
                        setJoinKeys((current) => [
                          ...current.filter((item) => sourceKey(item) !== sourceKey(profile)),
                          ...(reference ? [reference] : []),
                        ]);
                      }}
                    >
                      <MenuItem value=""><em>Choose a column</em></MenuItem>
                      {profileOptions.map((option) => (
                        <MenuItem key={refKey(option)} value={refKey(option)}>
                          {option.column}
                          <span style={{ marginLeft: "auto", color: "#64748b", paddingLeft: 12 }}>
                            {option.distinctCount.toLocaleString()} unique · {option.coverage}% filled
                          </span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              );
            })}
          </Stack>
        </>}

        {step === 1 && <>
          <Alert severity={columnsReady ? "success" : "warning"} sx={{ mb: 2 }}>
            These columns will be combined into one merchant record. Remove anything you do not need; matching columns are kept by default.
          </Alert>
          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1.25, display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#fff" }}>
              <Typography sx={{ fontWeight: 900, color: "#0b2a4a" }}>
                {retainedColumns.length} of {options.length} columns kept
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setRetainedColumns(preview?.combinedColumns || options)}>Keep all</Button>
                <Button size="small" onClick={() => setRetainedColumns(joinKeys)}>Matching only</Button>
              </Stack>
            </Box>
            <Box sx={{ maxHeight: 430, overflowY: "auto", borderTop: "1px solid #e2e8f0" }}>
              {profiles.map((profile) => (
                <Box key={sourceKey(profile)} sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                  <Typography sx={{ fontWeight: 850, color: "#17365f", mb: 0.75 }}>
                    {profile.fileName} · {profile.sheetName}
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 0.25 }}>
                    {options.filter((option) => sourceKey(option) === sourceKey(profile)).map((option) => {
                      const combined = (preview?.combinedColumns || []).find((item) => refKey(item) === refKey(option)) || option;
                      const selected = retainedColumns.some((item) => refKey(item) === refKey(option));
                      const isJoin = joinKeys.some((item) => refKey(item) === refKey(option));
                      return (
                        <FormControlLabel
                          key={refKey(option)}
                          control={<Checkbox checked={selected} onChange={(event) => setRetainedColumns((current) => (
                            event.target.checked
                              ? [...current, combined]
                              : current.filter((item) => refKey(item) !== refKey(option))
                          ))} />}
                          label={<span>{option.column}{isJoin ? " · matching key" : ""}</span>}
                        />
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </>}

        {step === 2 && <>
        <Alert severity={ready ? "success" : "info"} sx={{ mb: 2 }}>
          {ready
            ? `Ready to import ${retainedColumns.length} columns. ${mappedCount} model fields mapped${readyOutputs.length ? ` and ${readyOutputs.length} optional output model${readyOutputs.length === 1 ? "" : "s"} configured` : ""}.`
            : "Model fields and outputs are optional. Complete any enabled output, then import the unified merchant table."}
        </Alert>
        {["Identity", "Advance & pricing", "ML features"].map((group) => (
          <Paper key={group} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "#0b2a4a", mb: 0.5 }}>{group}</Typography>
            <Typography sx={{ color: "#64748b", fontSize: 12.5, mb: 1 }}>
              {group === "Identity" && "Columns used to resolve the same merchant across every uploaded file."}
              {group === "Advance & pricing" && "Optional requested or funded advance size, payment volume, term and pricing."}
              {group === "ML features" && "Underwriting and platform signals available at decision time."}
            </Typography>
            {mappingFields.filter((field) => field.group === group).map((field) => (
              <FieldSelect
                key={field.key}
                field={field}
                value={fields[field.key]}
                options={options}
                onChange={(reference) => setFields((current) => ({ ...current, [field.key]: reference }))}
              />
            ))}
          </Paper>
        ))}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start", mb: 1.5 }}>
            <Box>
          <Typography sx={{ fontWeight: 900, color: "#0b2a4a" }}>Model output labels</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 12.5, mb: 1.5 }}>
            Optional. Enable outputs only when an outcome or funded-results file is included.
          </Typography>
            </Box>
            <FormControlLabel control={<Switch checked={runAll} onChange={(event) => setRunAll(event.target.checked)} />} label="Run all configured outputs" />
          </Box>
          <Stack spacing={1.5}>
          {outputs.map((output) => {
            const selectedOption = options.find((option) => refKey(option) === refKey(output)) || null;
            return (
            <Paper key={output.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: output.enabled ? "#fff" : "#f8fafc" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "center", mb: output.enabled ? 1.5 : 0 }}>
                <Box>
                  <Typography sx={{ fontWeight: 850, color: "#17365f" }}>{output.name}</Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 11.5 }}>
                    {output.type === "boolean" ? "Boolean classification" : output.type === "ordinal" ? "Native ordinal score · 1–5" : "Numeric regression · currency amount"}
                  </Typography>
                </Box>
                <FormControlLabel control={<Switch checked={output.enabled} onChange={(event) => updateOutput(output.id, { enabled: event.target.checked })} />} label={output.enabled ? "Included" : "Not included"} />
              </Box>
              {output.enabled && <>
                <FormControl fullWidth size="small" sx={{ mb: output.type === "boolean" ? 1.5 : 0 }}>
                  <InputLabel>Output column</InputLabel>
                  <Select label="Output column" value={refKey(output)} onChange={(event) => chooseOutputColumn(output, options.find((option) => refKey(option) === event.target.value) || null)}>
                    <MenuItem value=""><em>Not mapped</em></MenuItem>
                    {options.map((option) => <MenuItem key={refKey(option)} value={refKey(option)}>{option.fileName} · {option.sheetName} · {option.column}<span style={{ marginLeft: "auto", color: "#64748b", paddingLeft: 12 }}>{option.distinctCount} values</span></MenuItem>)}
                  </Select>
                </FormControl>
                {output.type === "boolean" && <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.5 }}>
            <FormControl fullWidth size="small" disabled={!selectedOption}>
              <InputLabel>Positive / performing values</InputLabel>
              <Select
                multiple
                label="Positive / performing values"
                value={output.positiveValues || []}
                onChange={(event) => {
                  const next = event.target.value;
                  updateOutput(output.id, { positiveValues: next, negativeValues: (output.negativeValues || []).filter((value) => !next.includes(value)) });
                }}
                renderValue={(selected) => selected.join(", ")}
              >
                {(selectedOption?.values || []).map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    <Checkbox checked={(output.positiveValues || []).includes(item.value)} />
                    <ListItemText primary={item.value} secondary={`${item.count.toLocaleString()} rows`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!selectedOption}>
              <InputLabel>Negative / non-performing values</InputLabel>
              <Select
                multiple
                label="Negative / non-performing values"
                value={output.negativeValues || []}
                onChange={(event) => {
                  const next = event.target.value;
                  updateOutput(output.id, { negativeValues: next, positiveValues: (output.positiveValues || []).filter((value) => !next.includes(value)) });
                }}
                renderValue={(selected) => selected.join(", ")}
              >
                {(selectedOption?.values || []).map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    <Checkbox checked={(output.negativeValues || []).includes(item.value)} />
                    <ListItemText primary={item.value} secondary={`${item.count.toLocaleString()} rows`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>}
          {output.type === "ordinal" && <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 1.5 }}><TextField size="small" type="number" label="Minimum score" value={output.min} onChange={(event) => updateOutput(output.id, { min: Number(event.target.value) })} /><TextField size="small" type="number" label="Maximum score" value={output.max} onChange={(event) => updateOutput(output.id, { max: Number(event.target.value) })} /></Box>}
          </>}
            </Paper>
          );})}
          </Stack>
        </Paper>
        </>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        {step > 0 && <Button onClick={() => setStep((value) => value - 1)} disabled={busy}>Back</Button>}
        {step < 2 && (
          <Button
            variant="contained"
            onClick={continueToNextStep}
            disabled={busy || (step === 0 ? !joinReady : !columnsReady)}
          >
            Continue
          </Button>
        )}
        {onSaveDraft && (
          <Button
            variant="outlined"
            onClick={() => onSaveDraft(mappingPayload())}
            disabled={busy || mappedCount === 0}
          >
            {busy ? "Saving…" : "Save mapping"}
          </Button>
        )}
        {step === 2 && <Button variant="contained" onClick={apply} disabled={busy || !ready}>
          {busy ? "Importing…" : "Import unified merchant data"}
        </Button>}
      </DialogActions>
    </Dialog>
  );
}
